import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { signIn, signUp, confirmSignUp, resendCode, signInWithApple, getCurrentUser, getCachedUser } from '../services/auth';
import { isNative, isIOS } from '../services/platform';
import useStore from '../store';
import { metaRepo } from '../services/db/meta';
import { useActiveUser } from '../hooks/useActiveUser';
import Logo from '../components/Logo';

export default function Login() {
  const navigate = useNavigate();
  const setUser = useStore((s) => s.setUser);
  const { isAuthed } = useActiveUser();
  const [mode, setMode] = useState('signin'); // signin | signup | confirm
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasLocalData, setHasLocalData] = useState(false);

  useEffect(() => {
    metaRepo.getOnboardedAt().then((onboardedAt) => setHasLocalData(!!onboardedAt));
  }, []);

  useEffect(() => {
    if (isAuthed) navigate('/', { replace: true });
  }, [isAuthed, navigate]);

  async function handleAppleSignIn() {
    setLoading(true);
    try {
      await signInWithApple();
      if (isNative() && isIOS()) {
        // Native flow returns synchronously with tokens injected — advance the UI now.
        // Web flow has redirected to Cognito Hosted UI and will return via /auth/callback.
        // getCurrentUser() can momentarily return null right after token injection
        // (Amplify may have cached an empty in-memory session), so fall back to the
        // freshly-written tokens rather than falsely reporting failure.
        const user = (await getCurrentUser()) || getCachedUser();
        if (!user) throw new Error('Sign in succeeded but session is unavailable');
        setUser(user);
        navigate('/');
      }
    } catch (err) {
      toast.error(err.message || 'Apple Sign In failed');
      setLoading(false);
    }
  }

  async function handleResendCode() {
    try {
      await resendCode(email);
      toast.success('A new code is on its way');
    } catch (err) {
      toast.error(err.message || 'Could not resend code');
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'signup') {
        await signUp(email, password);
        toast.success('Check your email for a verification code');
        setMode('confirm');
      } else if (mode === 'confirm') {
        await confirmSignUp(email, code);
        toast.success('Account verified! Signing in...');
        const result = await signIn(email, password);
        if (result.isSignedIn) {
          const user = (await getCurrentUser()) || getCachedUser();
          setUser(user);
          navigate('/onboarding');
        } else {
          // Verified, but the auto sign-in needs another step (or the password
          // wasn't retained). Send them to sign in explicitly rather than
          // leaving them stuck on the confirm screen with no feedback.
          toast('Email verified — please sign in.');
          setMode('signin');
          setCode('');
        }
      } else {
        const result = await signIn(email, password);
        if (result.isSignedIn) {
          const { getCurrentUser } = await import('../services/auth');
          const user = await getCurrentUser();
          setUser(user);
          navigate('/');
        } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
          setMode('confirm');
          toast('Please verify your email first');
        }
      }
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-sm w-full">
        <div className="text-center mb-8">
          <Logo size={72} className="mb-4 shadow-lg" />
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
            Streaks
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {hasLocalData
              ? 'Sign in to back up your habits and sync across devices'
              : 'Build better habits, one day at a time'}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {mode === 'signin' && 'Sign In'}
            {mode === 'signup' && 'Create Account'}
            {mode === 'confirm' && 'Verify Email'}
          </h2>

          {mode !== 'confirm' && (
            <>
              <button
                type="button"
                onClick={handleAppleSignIn}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-black hover:bg-gray-900 text-white rounded-lg py-2.5 px-4 font-medium mb-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Sign in with Apple
              </button>
              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">or continue with email</span>
                </div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode !== 'confirm' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    required
                    autoComplete="email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                    required
                    minLength={8}
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  />
                </div>
              </>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  required
                  placeholder="123456"
                  autoComplete="one-time-code"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 px-4 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  {mode === 'signup' ? 'Creating...' : mode === 'confirm' ? 'Verifying...' : 'Signing in...'}
                </span>
              ) : (
                <>
                  {mode === 'signin' && 'Sign In'}
                  {mode === 'signup' && 'Create Account'}
                  {mode === 'confirm' && 'Verify'}
                </>
              )}
            </button>
          </form>

          {mode !== 'confirm' && (
            <div className="mt-4 text-center">
              <button
                onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                {mode === 'signin'
                  ? "Don't have an account? Sign up"
                  : 'Already have an account? Sign in'}
              </button>
            </div>
          )}

          {mode === 'confirm' && (
            <div className="mt-4 text-center space-y-2">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={loading}
                className="block w-full text-sm text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50"
              >
                Didn't get a code? Resend
              </button>
              <button
                type="button"
                onClick={() => { setMode('signin'); setCode(''); }}
                className="block w-full text-sm text-gray-500 dark:text-gray-400 hover:underline"
              >
                Back to sign in
              </button>
            </div>
          )}
        </div>

        {hasLocalData && mode !== 'confirm' && (
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-sm text-gray-500 dark:text-gray-400 hover:underline"
            >
              Continue without an account
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
